import { jsx, jsxs } from "react/jsx-runtime";
import {
  CounterBadge,
  __css,
  mergeClasses,
  tokens,
  Tooltip
} from "@fluentui/react-components";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { appShortcut } from "../shortcuts/registry.js";
import { useShortcut } from "../shortcuts/react.js";
import { useShell } from "./app-shell.js";
import { brandTokens } from "../theme/themes.js";
import { layout } from "../theme/tokens.js";
import { edge } from "./styles.js";
const visuallyHidden = {
  position: "absolute",
  inlineSize: "1px",
  blockSize: "1px",
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap"
};
const ITEM_PITCH = 48;
const RAIL_RESERVED = 8 + 13;
const useStyles = __css({"rail":{"Bnnss6s":"fi64zpg","Bns2gvi":"fbe3i81","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","B7ck84d":"f1ewtqcl","De3pzq":"f16xq7d1","Bmxbyg5":"f1sil6mw"},"bottom":{"Bnnss6s":"fi64zpg","De3pzq":"fxugw4r","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"foi3q75","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1rktw8j","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k"},"list":{"dclx09":"ftrb29c","jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Byoj8tv":0,"uwmqm3":0,"z189sj":0,"z8tnut":0,"B0ocmuz":"f1mk8lai","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Bt984gj":"f122n59","i8kkvl":0,"Belr9w4":0,"rmohyg":"f4xv25i","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k"},"bottomList":{"Beiy3e4":"f1063pyq","Bt984gj":"f1q9h2pe","Brf1p80":"f1iw3tyt","i8kkvl":0,"Belr9w4":0,"rmohyg":"f18ydsq9","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fx1xsyp"},"firstSecondary":{"Cgygva":"f1jtkyot","rjzwhg":"fhbwyth","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"foi3q75"},"item":{"qhf8xq":"f10pi13n","mc9l5x":"f22iagw","Bt984gj":"f122n59","Brf1p80":"f4d9j23","Bns2gvi":"f1byccpn","st59p4":"f19q6nmx","sj55zd":"fkfq4zb","w71qe1":"f1iuv45f","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","Bi91k9c":"fax3udt","Jwef8y":"f1h648pw","Beohmeb":"f1u5sdlw","B486eqv":"f1u8i3ug","vu0emg":"fvi4nxw","Byl69ji":"fz7cnr3"},"bottomItem":{"Bh6795r":"fqerorx","Beiy3e4":"f1vx9l62","i8kkvl":0,"Belr9w4":0,"rmohyg":"f1t6b6ee","Bns2gvi":"f1kdp0ux","st59p4":"fh05iye","qttt6h":"ff3ufwa","Bgto7am":"f1nny5rl","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1bs9zne","Be2twd7":"f13mqy1h","Bg96gwp":"fcpl73t","Bhrd7zp":"fdj6btp"},"active":{"sj55zd":"fulqd5u","De3pzq":"f1bgiulx","Bhrd7zp":"fl43uef","Bi91k9c":"f1p45l4d","Jwef8y":"f1rlav19"},"label":{"qhf8xq":"f1euv43f","Bns2gvi":"fittdk8","st59p4":"f156r0dy","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","Bgl5zvf":"f1wch0ki","Huce71":"fz5stix"},"bottomLabel":{"D305fb":"f1jd32q8","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","ygn44y":"f1cmbuwj","Huce71":"fz5stix"},"bottomCell":{"mc9l5x":"f22iagw","Bh6795r":"fqerorx"},"iconWrap":{"qhf8xq":"f10pi13n","mc9l5x":"ftuwxu6"},"badge":{"qhf8xq":"f1euv43f","Bwk1v6v":"f3o6osc","B3s2vv4":"f1qbguyp"}});
function AppBarShortcut({
  item,
  onNavigate
}) {
  useShortcut(appShortcut(item.shortcut ?? 0), () => {
    onNavigate(item);
  });
  return null;
}
function useRailSlots(enabled) {
  const ref = useRef(null);
  const [slots, setSlots] = useState(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return void 0;
    const measure = () => {
      const height = element.clientHeight;
      setSlots(height > 0 ? Math.floor((height - RAIL_RESERVED) / ITEM_PITCH) : null);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return void 0;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [enabled]);
  return { ref, slots };
}
function AppBar({
  items,
  secondaryItems,
  overflowItemId,
  activeId,
  onNavigate
}) {
  const styles = useStyles();
  const { t } = useTranslation("ui");
  const { tier, dir } = useShell();
  const phone = tier === "phone";
  const secondary = secondaryItems ?? [];
  const managesOverflow = !phone && secondaryItems !== void 0 && overflowItemId !== void 0;
  const { ref: navRef, slots } = useRailSlots(managesOverflow);
  let visible;
  let firstSecondaryIndex = -1;
  if (phone) {
    visible = items.filter((i) => i.onPhone);
  } else if (!managesOverflow) {
    visible = items;
  } else {
    const apps = items.filter((i) => i.id !== overflowItemId);
    const overflow = items.find((i) => i.id === overflowItemId);
    const capacity = slots ?? Number.POSITIVE_INFINITY;
    const fitsAll = apps.length + secondary.length <= capacity;
    const shown = fitsAll ? secondary : secondary.slice(0, Math.max(0, capacity - apps.length - 1));
    firstSecondaryIndex = shown.length > 0 ? apps.length : -1;
    visible = [...apps, ...shown, ...!fitsAll && overflow ? [overflow] : []];
  }
  const hiddenActive = managesOverflow && activeId !== void 0 && secondary.some((i) => i.id === activeId) && !visible.some((i) => i.id === activeId);
  const currentId = hiddenActive ? overflowItemId : activeId;
  const links = useRef([]);
  const activeIndex = Math.max(
    0,
    visible.findIndex((i) => i.id === currentId)
  );
  const [focusIndex, setFocusIndex] = useState(null);
  const tabStop = focusIndex !== null && focusIndex < visible.length ? focusIndex : activeIndex;
  const move = (index) => {
    const count = visible.length;
    const next = (index % count + count) % count;
    setFocusIndex(next);
    links.current[next]?.focus();
  };
  const onKeyDown = (event) => {
    const current = links.current.findIndex((el) => el === event.target);
    if (current < 0) return;
    const forward = phone ? dir === "rtl" ? "ArrowLeft" : "ArrowRight" : "ArrowDown";
    const backward = phone ? dir === "rtl" ? "ArrowRight" : "ArrowLeft" : "ArrowUp";
    if (event.key === forward) move(current + 1);
    else if (event.key === backward) move(current - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(visible.length - 1);
    else return;
    event.preventDefault();
  };
  const onClick = (event, item) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    onNavigate(item);
  };
  return /* @__PURE__ */ jsxs(
    "nav",
    {
      ref: navRef,
      "aria-label": phone ? t("shell.bottomNav") : t("shell.appBarLabel"),
      className: phone ? styles.bottom : styles.rail,
      children: [
        items.filter((i) => i.shortcut !== void 0 && i.shortcut >= 1 && i.shortcut <= 9).map((item) => /* @__PURE__ */ jsx(AppBarShortcut, { item, onNavigate }, item.id)),
        /* @__PURE__ */ jsx("ul", { className: mergeClasses(styles.list, phone && styles.bottomList), children: visible.map((item, index) => {
          const active = item.id === currentId;
          const Icon = active && item.activeIcon ? item.activeIcon : item.icon;
          const badge = item.badge ?? 0;
          const link = /* @__PURE__ */ jsxs(
            "a",
            {
              ref: (el) => {
                links.current[index] = el;
              },
              tabIndex: index === tabStop ? 0 : -1,
              onFocus: () => {
                setFocusIndex(index);
              },
              onKeyDown,
              href: item.href,
              "aria-current": active ? "page" : void 0,
              className: mergeClasses(
                styles.item,
                phone && styles.bottomItem,
                active && styles.active
              ),
              onClick: (e) => {
                onClick(e, item);
              },
              children: [
                /* @__PURE__ */ jsxs("span", { className: styles.iconWrap, children: [
                  /* @__PURE__ */ jsx(Icon, { fontSize: phone ? 20 : 24, "aria-hidden": true }),
                  badge > 0 && /* @__PURE__ */ jsx(
                    CounterBadge,
                    {
                      className: styles.badge,
                      count: badge,
                      size: "small",
                      overflowCount: 99,
                      "aria-label": t("shell.badge", { count: badge })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx("span", { className: phone ? styles.bottomLabel : styles.label, children: item.label })
              ]
            }
          );
          return /* @__PURE__ */ jsx(
            "li",
            {
              className: mergeClasses(
                phone && styles.bottomCell,
                index === firstSecondaryIndex && styles.firstSecondary
              ),
              children: phone ? link : /* @__PURE__ */ jsx(Tooltip, { content: item.label, relationship: "inaccessible", positioning: "after", children: link })
            },
            item.id
          );
        }) })
      ]
    }
  );
}
export {
  AppBar
};
//# sourceMappingURL=app-bar.js.map

import "/opt/batiflow/app/packages/ui/dist/shell/app-bar.griffel.css";